import React from "react";
import { X } from "lucide-react";
import FeedLiveChatPanel from "./FeedLiveChatPanel";
import FeedFeaturedVideosPanel from "./FeedFeaturedVideosPanel";
import FeedPlayerControlsBar from "./FeedPlayerControlsBar";
import FeedNowPlayingPanel from "./FeedNowPlayingPanel";
import FeedVideoPlayer from "./FeedVideoPlayer";
import FeedCommentsPanel from "./FeedCommentsPanel";
import FeedTipBlock from "./FeedTipBlock";
import JoinHereBox from "../shared/JoinHereBox";
import FeedNearbyVideosPanel from "./FeedNearbyVideosPanel";
import FeedNearbyProducersPanel from "./FeedNearbyProducersPanel";
import FeedPrompts from "./FeedPrompts";
import FeedSearchBar from "./FeedSearchBar";

// FeedPlayerOverlay (renamed to PlayerOverlay in spirit — kept the
// FeedPlayerOverlay export for now so the home page doesn't churn).
//
// Universal player shell used by:
//   - Home (mode='home')     /  ← global feed, region tabs, source toggle visible
//   - Following (mode='following')  /feed  ← Following feed only
//   - Profile (mode='profile') /:username ← single creator's videos, close button
//
// All state stays in the calling page. This component is pure
// presentation + composition.
const FeedPlayerOverlay = ({
  // Mode controls which auxiliary controls render. Defaults to 'home'
  // for backwards compatibility — existing FeedPage callers don't need
  // to pass this.
  mode = 'home',
  // Optional close handler — when provided, renders a floating ✕ in
  // the top-right. Used by ProfilePage to dismiss the player without
  // leaving the route.
  onCloseOverlay,
  // Playback
  videoRef,
  userPausedRef,
  activeVideos,
  videos,
  currentVideoIndex,
  setCurrentVideoIndex,
  isLoopMode,
  setIsLoopMode,
  source,
  setSource,
  activeRegion,
  setActiveRegion,
  failedIdsRef,
  exhaustedToastShownRef,
  findNextPlayableIndex,
  // Handlers
  handleClosePlayer,
  handleVideoEnded,
  handleSkipNext,
  handleShare,
  handleApplaud,
  handleFollowFromFeed,
  handleSubmitComment,
  handleSubmitReply,
  handleSubmitHomeChat,
  handleDeleteHomeChat,
  // Comments
  comments,
  loadingComments,
  newComment,
  setNewComment,
  submittingComment,
  // Home Live Chat
  homeChat,
  newHomeChat,
  setNewHomeChat,
  submittingHomeChat,
  liveChatTtlDays,
  // Side data
  featuredVideos,
  followingUsers,
  trailIds,
  handleToggleTrail,
  // Prompts
  showSignInPrompt,
  setShowSignInPrompt,
  pendingAction,
  setPendingAction,
  showCreditsPrompt,
  setShowCreditsPrompt,
  creditsPromptConfig,
  // Misc
  user,
  navigate,
}) => {
  return (
    <div
      className="bg-black relative
                 min-h-[calc(100dvh-3.5rem-env(safe-area-inset-top,0px))]
                 sm:min-h-[calc(100dvh-4rem-env(safe-area-inset-top,0px))]
                 lg:min-h-0 lg:h-[calc(100dvh-4rem-env(safe-area-inset-top,0px))]
                 lg:overflow-hidden
                 pb-[calc(env(safe-area-inset-bottom,0px)+6rem)]
                 lg:pb-[env(safe-area-inset-bottom,0px)]"
      data-testid="home-player-overlay"
      data-player-mode={mode}
    >
      {onCloseOverlay && (
        <button
          type="button"
          onClick={onCloseOverlay}
          className="absolute top-3 right-3 z-30 text-white hover:text-gray-300 bg-gray-800/80 hover:bg-gray-700 rounded-full p-2 transition-colors backdrop-blur-sm"
          data-testid="player-close-overlay-btn"
          aria-label="Close player"
        >
          <X className="h-5 w-5" />
        </button>
      )}
      <div className="px-3 sm:px-4 pt-2 pb-3 lg:h-full lg:pb-1 lg:pt-1">
        <div className="flex flex-col lg:flex-row lg:justify-center gap-2 max-w-7xl w-full mx-auto items-stretch lg:h-full">

          {/* LEFT — One connected card: Title meta → Comments → Tip.
          {/* LEFT — title meta → comments → tip CTA.
              Mobile order-2 (below the video block); desktop lg:order-1
              (back to the left of the player). */}
          <div className="w-full lg:w-64 order-2 lg:order-1 flex-shrink-0 bg-gray-900/80 rounded-lg flex flex-col divide-y divide-gray-800/80 overflow-hidden lg:min-h-0 lg:h-full" data-testid="feed-left-column">
            <FeedNowPlayingPanel
              activeVideos={activeVideos}
              currentVideoIndex={currentVideoIndex}
              user={user}
              followingUsers={followingUsers}
              handleClosePlayer={handleClosePlayer}
              handleFollowFromFeed={handleFollowFromFeed}
            />
            <FeedCommentsPanel
              activeVideos={activeVideos}
              currentVideoIndex={currentVideoIndex}
              user={user}
              comments={comments}
              loadingComments={loadingComments}
              newComment={newComment}
              setNewComment={setNewComment}
              submittingComment={submittingComment}
              handleClosePlayer={handleClosePlayer}
              handleApplaud={handleApplaud}
              handleSubmitComment={handleSubmitComment}
              handleSubmitReply={handleSubmitReply}
              setShowSignInPrompt={setShowSignInPrompt}
              setPendingAction={setPendingAction}
              trailIds={trailIds}
              handleToggleTrail={handleToggleTrail}
            />
            {/* Profile-mode only: the profile URL IS the referral link.
                Sits above the Tip block so the audience sees the clean
                /username address before any other monetisation CTA. */}
            {mode === 'profile' && (
              <JoinHereBox profileUsername={activeVideos[currentVideoIndex]?.username} variant="compact" />
            )}
            <FeedTipBlock
              activeVideos={activeVideos}
              currentVideoIndex={currentVideoIndex}
              user={user}
              handleClosePlayer={handleClosePlayer}
              navigate={navigate}
            />
          </div>

          {/* Center "column" — `display: contents` on mobile so its
              three children (video block, live chat, featured) flow
              UP as direct flex items of the outer flex-col parent.
              That lets us interleave them with the left+right columns
              via the `order-X` classes below.
              Iter-58.2 (May 30, 2026) mobile order:
                Video → Title/Comments/Tip → Nearby V+P → Live Chat → Featured
              On desktop (lg:flex) the wrapper switches back to a real
              flex container, restoring the 3-column side-by-side layout
              with video+chat+featured stacked vertically inside it. */}
          <div className="contents lg:flex lg:w-[672px] lg:flex-col lg:items-stretch lg:justify-start lg:order-2 lg:min-w-0 lg:min-h-0 lg:h-full">
            {/* VIDEO + controls block — mobile order-1 (top of page) */}
            <div className="w-full max-w-2xl mx-auto order-1 lg:order-none flex flex-col items-stretch flex-shrink-0">
              <div className="w-full text-center mb-0.5">
                <span className="text-gray-400 text-xs leading-none">/{activeVideos[currentVideoIndex]?.username}</span>
              </div>
              <FeedVideoPlayer
                videoRef={videoRef}
                userPausedRef={userPausedRef}
                activeVideos={activeVideos}
                currentVideoIndex={currentVideoIndex}
                setCurrentVideoIndex={setCurrentVideoIndex}
                source={source}
                activeRegion={activeRegion}
                failedIdsRef={failedIdsRef}
                exhaustedToastShownRef={exhaustedToastShownRef}
                findNextPlayableIndex={findNextPlayableIndex}
                handleVideoEnded={handleVideoEnded}
                handleSkipNext={handleSkipNext}
              />
              <FeedPlayerControlsBar
                user={user}
                isLoopMode={isLoopMode}
                setIsLoopMode={setIsLoopMode}
                source={source}
                setSource={setSource}
                activeRegion={activeRegion}
                setActiveRegion={setActiveRegion}
                setCurrentVideoIndex={setCurrentVideoIndex}
                activeVideos={activeVideos}
                currentVideoIndex={currentVideoIndex}
                handleSkipNext={handleSkipNext}
                handleShare={handleShare}
                mode={mode}
              />
            </div>

            {/* LIVE CHAT — mobile order-4 (after Nearby right column); desktop
                resumes natural DOM order inside the center column.
                Jun 9 2026: Featured Videos moved out to the right column,
                so Live Chat now claims the full vertical span (its
                `flex-1` already grows to consume the remaining height
                left by the video player + controls + search bar above).
                Explicit mobile `min-h-[320px]` (was 260) so the chat
                gets enough room to feel like the page's primary
                conversation pane. */}
            <div className="w-full max-w-2xl mx-auto mt-1.5 lg:mt-1 order-4 lg:order-none flex-1 min-h-[320px] lg:min-h-0 flex flex-col gap-1.5">
              <FeedSearchBar
                navigate={navigate}
                handleClosePlayer={handleClosePlayer}
              />
              <div className="flex-1 min-h-0 flex">
                <FeedLiveChatPanel
                  user={user}
                  homeChat={homeChat}
                  liveChatTtlDays={liveChatTtlDays}
                  newHomeChat={newHomeChat}
                  setNewHomeChat={setNewHomeChat}
                  submittingHomeChat={submittingHomeChat}
                  handleApplaud={handleApplaud}
                  handleSubmitHomeChat={handleSubmitHomeChat}
                  handleDeleteHomeChat={handleDeleteHomeChat}
                  handleClosePlayer={handleClosePlayer}
                />
              </div>
            </div>
          </div>

          {/* RIGHT — Three equal-height panels (Jun 9 2026 layout change
              per Eric): Nearby Videos · Nearby Producers · Featured
              Videos. `divide-y` between siblings + each child given
              `flex-1` so the column splits exactly into thirds on
              desktop. Mobile keeps the column at `order-3` between the
              Tip block and Live Chat. Mobile `pl-10 lg:pl-0` reserves
              a left-edge gutter so the Emergent "Made With" badge
              (`fixed bottom-left`) can't chop the first letter of
              "Nearby Producers" / "Featured" headers when the user
              scrolls them into the badge's vertical band. */}
          <div className="w-full lg:w-64 order-3 lg:order-3 flex-shrink-0 bg-gray-900/80 rounded-lg flex flex-col divide-y divide-gray-800/80 overflow-hidden lg:min-h-0 lg:h-full pl-10 lg:pl-0" data-testid="feed-right-column">
            <div className="lg:flex-1 lg:min-h-0 flex">
              <FeedNearbyVideosPanel
                activeVideo={activeVideos[currentVideoIndex]}
                handleClosePlayer={handleClosePlayer}
              />
            </div>
            <div className="lg:flex-1 lg:min-h-0 flex">
              <FeedNearbyProducersPanel
                activeVideo={activeVideos[currentVideoIndex]}
                handleClosePlayer={handleClosePlayer}
              />
            </div>
            <div className="lg:flex-1 lg:min-h-0 flex">
              <FeedFeaturedVideosPanel
                featuredVideos={featuredVideos}
                navigate={navigate}
                handleClosePlayer={handleClosePlayer}
              />
            </div>
          </div>

        </div>
      </div>

      <FeedPrompts
        showSignInPrompt={showSignInPrompt}
        setShowSignInPrompt={setShowSignInPrompt}
        pendingAction={pendingAction}
        showCreditsPrompt={showCreditsPrompt}
        setShowCreditsPrompt={setShowCreditsPrompt}
        creditsPromptConfig={creditsPromptConfig}
        handleClosePlayer={handleClosePlayer}
      />
    </div>
  );
};

export default FeedPlayerOverlay;
